import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import dayjs from 'dayjs';

import { ButtonComponent } from '@shared/components/button/button.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { Currency, PermissionType, TrialImplementationStatus } from '@shared/services/gql.service';
import { GlobalStore } from '@shared/store/global/global.store';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { decimalAdd, decimalEquality } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { InvoiceBannerButtonDirective } from './invoice-detail-banner-button.directive';
import { InvoiceFormUtils } from '../invoice-form.utils';
import { InvoiceValidators } from '../services/invoice.validators';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [
    {
      directive: InvoiceBannerButtonDirective,
      inputs: ['invoice', 'invoiceFormComponent'],
    },
  ],
  imports: [ButtonComponent, TooltipDirective],
  selector: 'aux-invoice-detail-status-banner-save-button',
  template: `
    <aux-button
      [auxTooltip]="tooltip()"
      [disabled]="disabled()"
      [onClick]="onSave"
      icon="Pencil"
      variant="secondary"
      label="Save"
      data-pendo-id="invoice-detail-save-button"
    />
  `,
})
export class InvoiceDetailSaveBtnComponent {
  ctx = inject(InvoiceBannerButtonDirective);
  globalStore = inject(GlobalStore);
  organizationQuery = inject(OrganizationQuery);

  hasEditPermission = this.ctx.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_UPDATE_INVOICES],
  });

  disabled = computed(() => {
    return !this.hasEditPermission() || this.ctx.buttonDisabled();
  });
  isJEConfigSetToAttribute = computed(() => {
    return !!this.globalStore.primaryAttribute.attribute_name();
  });
  tooltip = computed(() => {
    if (!this.hasEditPermission()) {
      return this.ctx.dontHavePermission;
    }

    if (this.ctx.buttonDisabled()) {
      return this.ctx.invoiceLockTooltip();
    }

    return '';
  });

  // eslint-disable-next-line max-lines-per-function, complexity
  onSave = async () => {
    const { invoiceService, mainQuery, overlayService } = this.ctx;
    const currentOpenMonth = mainQuery.getValue().currentOpenMonth;
    const cmp = this.ctx.invoiceFormComponent();
    const trial = this.ctx.mainQuery.selectedTrial();

    if (!cmp) {
      return;
    }

    cmp.submit = true;

    cmp.invoiceFormRef().onSubmit({} as Event);
    cmp.refreshView();

    const oldInvoice = cmp.invoice();

    const invoice = InvoiceFormUtils.invoiceFormToInvoiceModel(
      cmp.invoiceForm.getRawValue(),
      oldInvoice
    );

    const errors = {
      accrual_period: false,
      services_period_trial_timeline: false,
      totals: false,
    };

    const invoiceTotal = invoice.expense_amounts.invoice_total;
    let otherTotals = 0;

    Object.entries(invoice.expense_amounts)
      .filter(([, expense]) => expense.type !== invoiceTotal.type)
      .forEach(([key, expense]) => {
        if (Utils.isNumber(expense.value) && !key.includes('_trial_currency')) {
          otherTotals = decimalAdd(otherTotals, Number(expense.value));
        }
      });

    if (
      !this.isJEConfigSetToAttribute() &&
      !decimalEquality(otherTotals, Number(invoiceTotal.value), 2)
    ) {
      errors.totals = true;
    }

    if (
      !this.ctx.isAccrualPeriodInClosedMonth() &&
      (trial?.implementation_status === TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED ||
        trial?.implementation_status === TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE)
    ) {
      if (oldInvoice.accrual_period !== invoice.accrual_period && invoice.accrual_period) {
        if (dayjs(`${invoice.accrual_period}-01`).isBefore(currentOpenMonth)) {
          errors.accrual_period = true;
          cmp.invoiceForm.controls.accrual_period.updateValueAndValidity();
        }
      }
    }

    if (oldInvoice.services_period !== invoice.services_period && invoice.services_period) {
      const { trialEndDate, trialStartDate } = mainQuery.getValue();

      const isServicesPeriodOutsideTrialTimeline = InvoiceValidators.validateServicesPeriod(
        invoice.services_period,
        trialStartDate,
        trialEndDate
      );

      if (isServicesPeriodOutsideTrialTimeline) {
        errors.services_period_trial_timeline = true;
        cmp.invoiceForm.controls.services_period.updateValueAndValidity();
      }
    }

    if (errors.totals || errors.accrual_period || errors.services_period_trial_timeline) {
      const message: string[] = [];

      if (errors.accrual_period) {
        message.push(
          `Invoice #${invoice.invoice_no || ''} - Posting Period cannot be set to a Closed Month.`
        );
      }

      if (errors.services_period_trial_timeline) {
        message.push(
          `Invoice #${invoice.invoice_no || ''} - Service Period must be set to a date in the trial timeline.`
        );
      }

      if (errors.totals) {
        message.push(
          'Invoice Total must equal the sum of',
          '- Investigator',
          '- Services',
          '- Discount',
          '- Pass-thru'
        );
      }

      overlayService.error(message, undefined, true);

      return false;
    }

    if (cmp.invoiceForm.invalid) {
      return;
    }

    const org = this.organizationQuery.getEntity(invoice.organization.id);

    if (!org) {
      return null;
    }

    await invoiceService.update({
      ...invoice,
      accrual_period: invoice.accrual_period ? `${invoice.accrual_period}-01` : null,
      organization: {
        currency: org.currency as Currency,
        id: invoice.organization.id,
        name: org.name as string,
      },
      previous_services_period: oldInvoice.services_period || null,
      services_period: invoice.services_period ? `${invoice.services_period}-01` : null,
    });
  };
}
